import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertTriangle, Wrench, Receipt, Printer } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { JobCardForm } from './JobCardForm';
import { JobCardFormValues } from './schema';
import {
  getJobCard,
  getJobCardConfig,
  createJobCard,
  updateJobCard,
  updateJobCardStatus,
} from '@/app/services/jobCardService';
import { JobCardCreate, JobCardUpdate, JobCardStatus } from '@/app/types/jobCard';
import { AxiosError } from 'axios';
import { StatusTimeline } from '../../components/StatusTimeline';
import { format } from 'date-fns';
import { printJobCard } from '@/app/services/jobCardPrintService';
import { useAuthStore } from '@/app/store/authStore';

const JobCardDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const user = useAuthStore((state) => state.user);

  const isEditMode = !!id;
  const initialVehicleId = searchParams.get('vehicle_id')
    ? parseInt(searchParams.get('vehicle_id')!, 10)
    : undefined;

  const getErrorMessage = (error: Error): string => {
    if (error instanceof AxiosError && error.response?.data?.detail) {
      return error.response.data.detail;
    }
    return error.message;
  };

  // Fetch job card config
  const {
    data: config,
    isLoading: isLoadingConfig,
    isError: isConfigError,
    error: configError,
  } = useQuery({
    queryKey: ['job-card-config'],
    queryFn: getJobCardConfig,
    staleTime: 15 * 60 * 1000, // 15 minutes
  });

  // Fetch job card data if editing
  const {
    data: jobCard,
    isLoading: isLoadingJobCard,
    isError: isJobCardError,
    error: jobCardError,
  } = useQuery({
    queryKey: ['job-card', id],
    queryFn: () => getJobCard(Number(id)),
    enabled: isEditMode,
  });

  // Create job card mutation
  const createMutation = useMutation({
    mutationFn: (data: JobCardCreate) => createJobCard(data),
    onSuccess: (newJobCard) => {
      queryClient.invalidateQueries({ queryKey: ['job-cards'] });
      toast.success(`Job Card ${newJobCard.document_number} created successfully`);
      navigate(`/workshop/job-cards/detail/${newJobCard.id}`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to create job card: ${getErrorMessage(error)}`);
    },
  });

  // Update job card mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: JobCardUpdate }) => updateJobCard(id, data),
    onSuccess: (updatedJobCard) => {
      queryClient.invalidateQueries({ queryKey: ['job-cards'] });
      queryClient.invalidateQueries({ queryKey: ['job-card', id] });
      toast.success(`Job Card ${updatedJobCard.document_number} updated successfully`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update job card: ${getErrorMessage(error)}`);
    },
  });

  // Update status mutation
  const statusMutation = useMutation({
    mutationFn: ({ id, status, notes }: { id: number; status: JobCardStatus; notes?: string }) =>
      updateJobCardStatus(id, { status, notes }),
    onSuccess: (updatedJobCard) => {
      queryClient.invalidateQueries({ queryKey: ['job-cards'] });
      queryClient.invalidateQueries({ queryKey: ['job-card', id] });
      const statusName = config?.statuses.find((s) => s.code === updatedJobCard.status)?.name || updatedJobCard.status;
      toast.success(`Status updated to ${statusName}`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update status: ${getErrorMessage(error)}`);
    },
  });


  const handleSubmit = (data: JobCardFormValues): void => {
    // Transform form data to API format
    const apiData: JobCardCreate | JobCardUpdate = {
      vehicle_id: data.vehicle_id,
      transaction_date: format(data.transaction_date, 'yyyy-MM-dd'),
      site_id: data.site_id || null,
      party_id: data.party_id || null,
      reference_number: data.reference_number || null,
      notes: data.notes || null,
      customer_complaint: data.customer_complaint || null,
      technician_observations: data.technician_observations || null,
      job_card_type: data.job_card_type || null,
      priority: data.priority,
      odometer_reading: data.odometer_reading || null,
      fuel_level: data.fuel_level || null,
      bay_number: data.bay_number || null,
      lead_technician_id: data.lead_technician_id || null,
      estimated_completion_date: data.estimated_completion_date
        ? format(data.estimated_completion_date, 'yyyy-MM-dd')
        : null,
      vehicle_checked_items: data.vehicle_checked_items || null,
      items: data.items?.map((item) => ({
        item_id: item.item_id,
        quantity: item.quantity,
        unit_id: item.unit_id,
        rate: item.rate,
        discount_percent: item.discount_percent || 0,
        tax_rate_id: item.tax_rate_id || null,
        notes: item.notes || null,
        technician_id: item.technician_id || null,
        estimated_duration_minutes: item.estimated_duration_minutes || null,
        completion_status: item.completion_status || 'pending',
      })),
    };

    if (isEditMode && id) {
      updateMutation.mutate({ id: Number(id), data: apiData });
    } else {
      createMutation.mutate(apiData as JobCardCreate);
    }
  };

  const handleStatusChange = async (newStatus: JobCardStatus, notes?: string): Promise<void> => {
    if (!id) return;
    statusMutation.mutate({ id: Number(id), status: newStatus, notes });
  };

  const handleConvertToInvoice = (): void => {
    if (!id || !jobCard) return;

    const invoiceData = {
      party_id: jobCard.party_id,
      site_id: jobCard.site_id,
      reference_number: jobCard.reference_number || '',
      narration: `From Job Card #${jobCard.document_number}`,
      items: jobCard.items?.map((item) => ({
        ...item,
        item_id: item.item_id,
        description: item.notes || '',
        quantity: item.quantity,
        unit_id: item.unit_id || 1,
        rate: item.rate,
        discount_percent: item.discount_percent || 0,
        discount_amount: item.discount_amount || 0,
        tax_rate_id: item.tax_rate_id,
      })) || [],
      transaction_date: new Date(),
      due_date: null,
      voucher_type_id: 1,
      total_amount: jobCard.total_amount,
    };

    navigate('/sales-invoices/new', {
      state: {
        duplicateInvoice: invoiceData,
        fromDuplicate: true,
        fromJobCard: true,
        jobCardId: Number(id),
        jobCardNumber: jobCard.document_number,
      }
    });
    toast.info('Job card data loaded. Review and save the invoice.');
  };

  const handlePrint = (): void => {
    if (!jobCard || !config) return;
    const orgName = user?.organization?.name || 'Organization';
    printJobCard(jobCard, config, orgName);
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const isTerminalStatus = jobCard?.status === 'invoiced' || jobCard?.status === 'cancelled';
  const canConvert = jobCard?.status === 'ready' || jobCard?.status === 'delivered';
  const showConvertButton = isEditMode && !isTerminalStatus;

  // Loading state
  if (isLoadingConfig || (isEditMode && isLoadingJobCard)) {
    return (
      <div className="mx-auto px-3 sm:px-6 lg:container py-3 sm:py-6 space-y-3 sm:space-y-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 sm:h-10 sm:w-10" />
          <Skeleton className="h-6 w-36 sm:h-8 sm:w-48" />
        </div>
        <Skeleton className="h-[400px] sm:h-[600px] w-full" />
      </div>
    );
  }

  // Error state - config
  if (isConfigError) {
    return (
      <div className="mx-auto px-3 sm:px-6 lg:container py-3 sm:py-6 space-y-3 sm:space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading configuration: {(configError as Error).message}
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate('/workshop/job-cards/list')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Job Cards
        </Button>
      </div>
    );
  }

  // Error state - job card
  if (isEditMode && isJobCardError) {
    return (
      <div className="mx-auto px-3 sm:px-6 lg:container py-3 sm:py-6 space-y-3 sm:space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading job card: {(jobCardError as Error).message}
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate('/workshop/job-cards/list')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Job Cards
        </Button>
      </div>
    );
  }

  if (!config) {
    return null;
  }

  return (
    <div className="mx-auto px-3 sm:px-6 lg:container py-3 sm:py-6 space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Button
            variant="outline"
            size="icon"
            className="shrink-0 h-9 w-9 sm:h-10 sm:w-10"
            onClick={() => navigate('/workshop/job-cards/list')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <Wrench className="h-5 w-5 sm:h-6 sm:w-6 shrink-0" />
            <h1 className="text-base sm:text-2xl font-bold truncate">
              {isEditMode ? `${jobCard?.document_number}` : 'New Job Card'}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {isEditMode && jobCard && (
            <Button
              variant="outline"
              onClick={handlePrint}
              className="h-10 sm:h-9 shrink-0"
            >
              <Printer className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Print</span>
            </Button>
          )}
          {showConvertButton && (
            <Button
              onClick={handleConvertToInvoice}
              disabled={!canConvert}
              title={!canConvert ? 'Move to Ready or Delivered status to convert' : undefined}
              className="w-full sm:w-auto h-10 sm:h-9 text-sm shrink-0"
            >
              <Receipt className="h-4 w-4 mr-2" />
              Convert to Invoice
            </Button>
          )}
        </div>
      </div>

      {/* Status Timeline (only in edit mode) */}
      {isEditMode && jobCard && (
        <Card>
          <CardHeader className="px-3 py-2 sm:px-6 sm:py-4">
            <CardTitle className="text-sm sm:text-base">Status</CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 sm:px-6 sm:pb-6">
            <StatusTimeline
              currentStatus={jobCard.status}
              config={config}
              onStatusChange={handleStatusChange}
              isUpdating={statusMutation.isPending}
              disabled={isTerminalStatus}
            />
            {jobCard.actual_completion_date && (
              <div className="mt-2 text-xs sm:text-sm text-muted-foreground flex items-center gap-4">
                <span>Completed: {format(new Date(jobCard.actual_completion_date), 'dd/MM/yyyy')}</span>
                {jobCard.estimated_completion_date && (
                  <span>Estimated: {format(new Date(jobCard.estimated_completion_date), 'dd/MM/yyyy')}</span>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Job Card Form */}
      <JobCardForm
        jobCard={jobCard}
        config={config}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        isEditMode={isEditMode}
        initialVehicleId={initialVehicleId}
        disabled={isTerminalStatus}
      />
    </div>
  );
};

export default JobCardDetailPage;
